
import { UrlTexture } from '../../render/Texture.js';

import type {
  LightmapSceneExportMaterialMaps,
  LightmapSceneExportMaterialSnapshot,
} from './lightmapSceneExportTypes.js';
import type * as THREE from 'three';

type SnapshotMaterial = THREE.Material & {
  color?: THREE.Color;
  roughness?: number;
  metalness?: number;
  emissive?: THREE.Color;
  emissiveIntensity?: number;
  map?: THREE.Texture | null;
  normalMap?: THREE.Texture | null;
  roughnessMap?: THREE.Texture | null;
  metalnessMap?: THREE.Texture | null;
  emissiveMap?: THREE.Texture | null;
  alphaMap?: THREE.Texture | null;
  aoMap?: THREE.Texture | null;
};

const textureKeys = [
  'map',
  'normalMap',
  'roughnessMap',
  'metalnessMap',
  'emissiveMap',
  'alphaMap',
  'aoMap',
] as const;

function colorTuple(color: THREE.Color | undefined): [number, number, number] | undefined {
  return color ? [color.r, color.g, color.b] : undefined;
}

function textureAssetPath(texture: THREE.Texture | null | undefined): string | undefined {
  if (texture instanceof UrlTexture && texture.url) {
    return texture.url;
  }
  return undefined;
}

function snapshotSingleMaterial(material: THREE.Material): LightmapSceneExportMaterialSnapshot | null {
  const source = material as SnapshotMaterial;
  const maps: LightmapSceneExportMaterialMaps = {};
  for (const key of textureKeys) {
    const url = textureAssetPath(source[key]);
    if (url) {
      maps[key] = url;
    }
  }

  const alphaMode: LightmapSceneExportMaterialSnapshot['alphaMode'] =
    material.transparent ? 'BLEND' : material.alphaTest > 0 ? 'MASK' : 'OPAQUE';

  const snapshot: LightmapSceneExportMaterialSnapshot = {
    ...(colorTuple(source.color) ? { color: colorTuple(source.color) } : {}),
    ...(material.opacity !== 1 ? { opacity: material.opacity } : {}),
    ...(material.transparent ? { transparent: true } : {}),
    ...(material.alphaTest > 0 ? { alphaTest: material.alphaTest } : {}),
    ...(typeof source.roughness === 'number' ? { roughness: source.roughness } : {}),
    ...(typeof source.metalness === 'number' ? { metalness: source.metalness } : {}),
    ...(colorTuple(source.emissive) ? { emissive: colorTuple(source.emissive) } : {}),
    ...(typeof source.emissiveIntensity === 'number' ? { emissiveIntensity: source.emissiveIntensity } : {}),
    ...(alphaMode !== 'OPAQUE' ? { alphaMode } : {}),
    ...(Object.keys(maps).length > 0 ? { maps } : {}),
  };

  return Object.keys(snapshot).length > 0 ? snapshot : null;
}

export function snapshotLightmapMaterial(
  material: THREE.Material | THREE.Material[],
): LightmapSceneExportMaterialSnapshot | null {
  const materials = Array.isArray(material) ? material : [material];
  const firstMaterial = materials[0];
  return firstMaterial ? snapshotSingleMaterial(firstMaterial) : null;
}
